import { useRef } from "react";
import { motion, useMotionValue, useSpring, useScroll, useTransform } from "framer-motion";
import { LineReveal, Eyebrow } from "./Motion";
import { CTA } from "./CTA";

const Badge = () => (
  <div className="kz-spin pointer-events-none absolute -left-2 -top-10 z-20 md:-left-10 h-28 w-28 md:h-36 md:w-36" aria-hidden>
    <svg viewBox="0 0 100 100" className="h-full w-full">
      <defs><path id="c" d="M50,50 m-38,0 a38,38 0 1,1 76,0 a38,38 0 1,1 -76,0" /></defs>
      <circle cx="50" cy="50" r="49" fill="#FDFFFF" stroke="#D4AF37" strokeWidth="0.6" />
      <text fontSize="7" letterSpacing="1.6" fill="#074E48" fontFamily="Montserrat" fontWeight="600">
        <textPath href="#c">SIMPLIFYING HR · AMPLIFYING BUSINESS ·</textPath>
      </text>
      <rect x="45" y="45" width="10" height="10" fill="#9E1B32" transform="rotate(45 50 50)" />
    </svg>
  </div>
);

const TiltFrame = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [6, -6]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 120, damping: 18 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={{ rotateX: rx, rotateY: ry, transformPerspective: 1200 }}
      initial={{ opacity: 0, scale: 0.94 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1], delay: 0.35 }}
      className="relative mx-auto w-full max-w-[520px]"
      data-testid="hero-visual"
    >
      <div className="absolute inset-0 translate-x-4 translate-y-4 border border-[#D4AF37]/60" aria-hidden />
      <div className="clip-corner relative aspect-[4/5] overflow-hidden bg-[#074E48]">
        <motion.img
          src="/img/hero_banner.jpg"
          alt="KRIMZON advisory leadership"
          style={{ y: imgY, scale: 1.18 }}
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#063A36]/70 via-transparent to-transparent" />
        <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between text-white">
          <span className="font-display text-xs uppercase tracking-[0.3em]">Excellence · Ease · Experience</span>
          <span className="h-2.5 w-2.5 rotate-45 bg-[#D4AF37]" />
        </div>
      </div>
      <Badge />
    </motion.div>
  );
};

export const Hero = () => (
  <section className="relative overflow-hidden pt-32 md:pt-40 lg:pt-44" data-testid="home-hero">
    <div className="grid-lines pointer-events-none absolute inset-0" aria-hidden />
    <div className="relative mx-auto grid max-w-[1400px] items-center gap-16 px-6 pb-20 lg:grid-cols-12 lg:gap-10 lg:px-12 lg:pb-32">
      <div className="lg:col-span-7">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }}>
          <Eyebrow>KRIMZON</Eyebrow>
        </motion.div>
        <LineReveal
          testId="hero-headline"
          delay={0.15}
          className="font-display mt-8 text-[2.6rem] font-extrabold leading-[1.02] tracking-[-0.03em] text-[#0D9488] sm:text-6xl lg:text-[5.4rem]"
          lines={["Simplifying HR.", "Amplifying", <span key="b">Business<span className="text-[#D4AF37]">.</span></span>]}
        />
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.7 }}
          className="font-display mt-8 text-xl font-semibold tracking-tight text-[#0D9488] md:text-2xl"
          data-testid="hero-tagline"
        >
          One partner across People, HR & Leadership.
        </motion.p>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.85 }}
          className="mt-6 max-w-xl text-base leading-relaxed text-[#2D6A64] md:text-lg"
          data-testid="hero-subtitle"
        >
          From building and managing your workforce to finding talent, developing people and transforming HR, KRIMZON unites process excellence, operational ease and enriching experiences under a single, accountable partnership.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1 }}
          className="mt-12 flex flex-wrap gap-4"
        >
          <CTA to="/services" testId="hero-cta-primary">Explore Our Solutions</CTA>
          <CTA variant="outline" testId="hero-cta-secondary">Talk to Krimzon</CTA>
        </motion.div>
      </div>
      <div className="lg:col-span-5">
        <TiltFrame />
      </div>
    </div>
  </section>
);
